import React, { useState } from "react";
import { X, Send, Loader2 } from "lucide-react";

const GhostAssistantButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState([
    {
      role: "assistant",
      content: "Hello! I'm Ghost™, your AI assistant powered by Prestigious Companies. How can I help you today?",
    },
  ]);

  const quickActions = [
    { label: "Learn about our brands", action: "Tell me about Prestigious Companies' divisions" },
    { label: "Contact information", action: "How can I contact Prestigious Companies?" },
    { label: "Support our mission", action: "How can I support your nonprofit work?" },
    { label: "Ghost™ Assistant features", action: "What can Ghost Assistant do for businesses?" },
  ];

  const handleSend = async (text = message) => {
    if (!text.trim()) return;
    
    const userMessage = { role: "user", content: text };
    setConversation((prev) => [...prev, userMessage]);
    setMessage("");
    setIsLoading(true);

    // Simulate AI response (in production, this would call your AI backend)
    setTimeout(() => {
      let response = "";
      const lowerText = text.toLowerCase();
      
      if (lowerText.includes("brand") || lowerText.includes("division") || lowerText.includes("compan")) {
        response = "Prestigious Companies operates 9 divisions: 1) Prestigious Travel Club Foundation (nonprofit), 2) Prestigious Travel Club (travel experiences), 3) Prestigious Marketing, 4) Prestigious Apps (technology), 5) Easy Quick Fund (financial services), 6) CreditForward360 (credit education & financial wellness), 7) Prestigious Hub (operations center), 8) Prestigious TV (streaming platform with 50,000+ channels), and 9) Ghost™ Assistant (AI platform). Each serves a unique purpose in our mission to empower communities. Would you like to learn more about any specific division?";
      } else if (lowerText.includes("contact")) {
        response = "You can reach us through our contact form on this website, or email info@prestigiouscompanies.org directly. We typically respond within 24-48 hours. Would you like me to help you navigate to the contact section?";
      } else if (lowerText.includes("support") || lowerText.includes("nonprofit")) {
        response = "Thank you for your interest in our nonprofit work! The Prestigious Travel Club Foundation partners with St. Jude, Feeding America, Wounded Warrior Project, and Disabled American Veterans to provide housing, food assistance, and veteran care to those in need. Contact us to learn more about how you can get involved.";
      } else if (lowerText.includes("ghost") || lowerText.includes("assistant") || lowerText.includes("ai")) {
        response = "Ghost™ Assistant is our proprietary AI platform built on a foundation of service and discipline—a tribute to those who have served. It provides intelligent automation, unified communications, 24/7 availability, and enterprise-grade security for businesses across all Prestigious Companies divisions.";
      } else if (lowerText.includes("hub")) {
        response = "Prestigious Hub is our central command center for operations, client management, training resources, and real-time business intelligence. It serves as the operational backbone connecting all Prestigious Companies divisions. Visit hub.prestigiouscompanies.org to learn more.";
      } else if (lowerText.includes("credit") || lowerText.includes("creditforward") || lowerText.includes("financial wellness")) {
        response = "CreditForward360 is our financial technology division — a modern credit education and financial wellness platform. It helps consumers understand, build, and strengthen their credit while providing tools that support long-term financial success. Visit creditforward360.com to start your journey to better credit!";
      } else if (lowerText.includes("tv") || lowerText.includes("stream") || lowerText.includes("channel") || lowerText.includes("entertainment")) {
        response = "Prestigious TV is our entertainment division — a powerful streaming platform offering access to over 50,000 channels worldwide. Enjoy live TV, movies, sports, and global entertainment all in one place. Visit Prestigious-TV.com to start streaming today!";
      } else {
        response = "I'm here to help you learn about Prestigious Companies and our services. You can ask me about our 9 divisions, how to contact us, ways to support our nonprofit mission, Prestigious TV streaming, CreditForward360 financial wellness, or our Ghost™ Assistant platform. What would you like to know?";
      }
      
      setConversation((prev) => [...prev, { role: "assistant", content: response }]);
      setIsLoading(false);
    }, 1000);
  };

  const handleQuickAction = (action) => {
    handleSend(action);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#0A192F] hover:bg-[#112240] text-white px-5 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl ${
          isOpen ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        data-testid="ghost-assistant-button"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_company-collective/artifacts/m6g4qkkl_image.png"
          alt="Ghost"
          className="w-8 h-8 object-contain"
        />
        <div className="text-left">
          <span className="block text-sm font-semibold">Ghost™</span>
          <span className="block text-[10px] text-white/70">Powered by Prestigious Companies</span>
        </div>
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
        data-testid="ghost-assistant-panel"
      >
        {/* Header */}
        <div className="bg-[#0A192F] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_company-collective/artifacts/m6g4qkkl_image.png"
              alt="Ghost"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h3 className="text-white font-semibold font-['Outfit']">Ghost™</h3>
              <p className="text-white/60 text-xs">Powered by Prestigious Companies</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="ghost-assistant-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Conversation */}
        <div className="h-[320px] overflow-y-auto p-4 space-y-4 bg-slate-50">
          {conversation.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-[#0A192F] text-white rounded-br-md"
                    : "bg-white text-[#0F172A] border border-slate-200 rounded-bl-md shadow-sm"
                }`}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white text-[#0F172A] border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
                  <span className="text-sm text-slate-500">Ghost™ is thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        {conversation.length <= 2 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-white">
            <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.action)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-[#0A192F] hover:text-white text-xs font-medium rounded-full transition-colors text-slate-700"
                  data-testid={`ghost-quick-action-${index}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              placeholder="Ask Ghost™ anything..."
              className="flex-1 px-4 py-2.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0A192F] focus:bg-white transition-all"
              data-testid="ghost-assistant-input"
            />
            <button
              onClick={() => handleSend()}
              disabled={!message.trim() || isLoading}
              className="p-2.5 bg-[#0A192F] hover:bg-[#112240] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-colors"
              data-testid="ghost-assistant-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GhostAssistantButton;
